import React from 'react';
import { Scatter } from 'react-chartjs-2';

const ScatterChart = ({
  chartConfig,
  chartTitle,
  summarizedData,
  chartData
}) => {
  // For scatter charts, use the selected X and Y axis fields with optional grouping
  const xAxisField = chartConfig.scatterXAxis;
  const yAxisField = chartConfig.scatterYAxis;
  const groupField = chartConfig.scatterGroupField;
  
  console.log('Scatter Chart Debug:', {
    xAxisField,
    yAxisField,
    groupField,
    chartData,
    summarizedData: summarizedData,
    metadata: summarizedData?._metadata
  });
  
  if (!xAxisField || !yAxisField) {
    return (
      <div className="chart-container">
        <div className="chart-title">{chartConfig.title || chartTitle}</div>
        <div className="chart-render" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="no-data-message">
            Please select both X-axis and Y-axis fields for the scatter chart
          </div>
        </div>
      </div>
    );
  }
  
  // Create scatter plot data using the selected fields
  let scatterDatasets = [];
  
  // Helper function to convert currency/number strings to numbers
  const parseValue = (valueStr) => {
    if (typeof valueStr === 'number') return valueStr;
    if (typeof valueStr === 'string') {
      // Remove currency symbols and commas, then parse
      const cleanValue = valueStr.replace(/[₹,$,%\s]/g, '');
      return parseFloat(cleanValue) || 0;
    }
    return 0;
  };
  
  // For scatter charts, we need to get the raw data from summarizedData
  if (summarizedData && summarizedData._metadata) {
    const metadata = summarizedData._metadata;
    const groupFields = metadata.group_fields || {};
    const summarizedFields = metadata.summarized_fields || {};
    
    console.log('Scatter Data Debug:', {
      groupFields,
      summarizedFields,
      xAxisField,
      yAxisField,
      groupField,
      allGroupFieldKeys: Object.keys(groupFields),
      allSummarizedFieldKeys: Object.keys(summarizedFields),
      actualDataKeys: Object.keys(summarizedData).filter(key => key !== '_metadata')
    });
    
    // Get the group field to use (either selected group field or first available)
    const targetGroupField = groupField || Object.keys(summarizedData).find(key => key !== '_metadata');
    
    console.log('Using group field:', targetGroupField);
    
    if (targetGroupField && summarizedData[targetGroupField]) {
      const groupData = summarizedData[targetGroupField];
      console.log('Group data:', groupData);
      
      // Extract field names from the dropdown selections
      // The dropdown shows "Salary (SUM)" but we need "salary"
      const xFieldName = xAxisField.toLowerCase().split(' ')[0]; // "Salary (SUM)" -> "salary"
      const yFieldName = yAxisField.toLowerCase().split(' ')[0]; // "Age (AVG)" -> "age"
      
      console.log('Extracted field names:', { xFieldName, yFieldName });
      
      // Create separate datasets for each group value with different colors
      const groupValues = Object.keys(groupData);
      const colors = [
        'rgba(255, 99, 132, 0.6)',   // Red
        'rgba(54, 162, 235, 0.6)',   // Blue
        'rgba(255, 205, 86, 0.6)',   // Yellow
        'rgba(75, 192, 192, 0.6)',   // Teal
        'rgba(153, 102, 255, 0.6)',  // Purple
        'rgba(255, 159, 64, 0.6)',   // Orange
        'rgba(199, 199, 199, 0.6)',  // Grey
        'rgba(83, 102, 255, 0.6)',   // Indigo
        'rgba(255, 99, 255, 0.6)',   // Pink
        'rgba(99, 255, 132, 0.6)'    // Green
      ];
      
      console.log('Processing group values for multiple series:', groupValues);
      
      groupValues.forEach((groupValue, index) => {
        const points = [];
        const groupRecord = groupData[groupValue];
        console.log(`Processing ${groupValue}:`, groupRecord);
        
        if (groupRecord && groupRecord[xFieldName] && groupRecord[yFieldName]) {
          const xValue = parseValue(groupRecord[xFieldName].value);
          const yValue = parseValue(groupRecord[yFieldName].value);
          
          console.log(`Extracted values for ${groupValue}:`, { xValue, yValue });
          
          if (xValue > 0 || yValue > 0) { // Only add points with valid data
            // Add the group value as a label for the point
            const pointData = { 
              x: xValue, 
              y: yValue,
              label: groupValue // Add group field value as label
            };
            console.log('Adding point data:', pointData);
            points.push(pointData);
          }
        }
        
        if (points.length > 0) {
          scatterDatasets.push({
            label: `${groupValue}`, // Each group gets its own legend entry
            data: points,
            backgroundColor: colors[index % colors.length],
            borderColor: colors[index % colors.length].replace('0.6', '1'),
            pointRadius: 6,
            pointHoverRadius: 8,
            borderWidth: 2,
            showLine: false
          });
        }
      });
    }
    
    // Handle multiple series if a different group field is selected
    if (groupField && groupField !== targetGroupField && summarizedData[groupField]) {
      // Multiple series - one for each group value
      const groupData = summarizedData[groupField];
      const groupValues = Object.keys(groupData);
      const colors = [
        'rgba(54, 162, 235, 0.6)',   // Blue
        'rgba(255, 99, 132, 0.6)',   // Red
        'rgba(255, 205, 86, 0.6)',   // Yellow
        'rgba(75, 192, 192, 0.6)',   // Teal
        'rgba(153, 102, 255, 0.6)',  // Purple
        'rgba(255, 159, 64, 0.6)',   // Orange
        'rgba(199, 199, 199, 0.6)',  // Grey
        'rgba(83, 102, 255, 0.6)'    // Indigo
      ];
      
      // Extract field names from the dropdown selections
      const xFieldName = xAxisField.toLowerCase().split(' ')[0];
      const yFieldName = yAxisField.toLowerCase().split(' ')[0];
      
      groupValues.forEach((groupValue, index) => {
        const points = [];
        const groupRecord = groupData[groupValue];
        
        if (groupRecord && groupRecord[xFieldName] && groupRecord[yFieldName]) {
          const xValue = parseValue(groupRecord[xFieldName].value);
          const yValue = parseValue(groupRecord[yFieldName].value);
          
          if (xValue > 0 || yValue > 0) {
            // Add the group value as a label for the point
            points.push({ 
              x: xValue, 
              y: yValue,
              label: groupValue // Add group field value as label
            });
          }
        }
        
        if (points.length > 0) {
          scatterDatasets.push({
            label: `${groupValue}`,
            data: points,
            backgroundColor: colors[index % colors.length],
            borderColor: colors[index % colors.length].replace('0.6', '1'),
            pointRadius: 6,
            pointHoverRadius: 8,
            borderWidth: 2,
            showLine: false
          });
        }
      });
    }
  } else {
    // Fallback: use the processed chartData
    console.log('Using fallback chartData approach');
    console.log('Available datasets:', chartData.datasets.map(d => d.label));
    
    const xFieldData = chartData.datasets.find(d => d.label.includes(xAxisField));
    const yFieldData = chartData.datasets.find(d => d.label.includes(yAxisField));
    
    console.log('Found datasets:', {
      xFieldData: xFieldData?.label,
      yFieldData: yFieldData?.label,
      xAxisField,
      yAxisField
    });
    
    if (xFieldData && yFieldData) {
      const points = [];
      chartData.labels.forEach((label, index) => {
        const xValue = parseFloat(xFieldData.data[index]) || 0;
        const yValue = parseFloat(yFieldData.data[index]) || 0;
        if (xValue > 0 || yValue > 0) { // Only add points with valid data
          points.push({ x: xValue, y: yValue });
        }
      });
      
      console.log('Generated points:', points);
      
      if (points.length > 0) {
        scatterDatasets.push({
          label: `${xAxisField} vs ${yAxisField}`,
          data: points,
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 2,
          showLine: false
        });
      }
    } else {
      console.log('No matching datasets found, trying alternative approach');
      
      // Try to create points from the first available dataset
      if (chartData.datasets.length > 0) {
        const firstDataset = chartData.datasets[0];
        const points = [];
        chartData.labels.forEach((label, index) => {
          const value = parseFloat(firstDataset.data[index]) || 0;
          if (value > 0) {
            points.push({ x: value, y: value });
          }
        });
        
        if (points.length > 0) {
          scatterDatasets.push({
            label: `${xAxisField} vs ${yAxisField}`,
            data: points,
            backgroundColor: 'rgba(54, 162, 235, 0.6)',
            borderColor: 'rgba(54, 162, 235, 1)',
            pointRadius: 6,
            pointHoverRadius: 8,
            borderWidth: 2,
            showLine: false
          });
        }
      }
    }
  }
  
  console.log('Final scatter datasets:', scatterDatasets);
  
  const scatterChartData = {
    datasets: scatterDatasets
  };
  
  // If no datasets were created, show a message
  if (scatterDatasets.length === 0) {
    console.log('No scatter datasets created - no data found');
  }
  
  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: chartConfig.legendPosition || 'bottom',
        display: scatterDatasets.length > 0 // Always show legend for scatter charts
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: chartConfig.showTooltips !== undefined ? chartConfig.showTooltips : true,
        callbacks: {
          label: function(context) {
            const point = context.parsed;
            const datasetLabel = context.dataset.label;
            const pointLabel = context.raw?.label || '';
            
            // Get field labels from metadata
            const xFieldLabel = summarizedData?._metadata?.summarized_fields?.[xAxisField]?.field_label || xAxisField;
            const yFieldLabel = summarizedData?._metadata?.summarized_fields?.[yAxisField]?.field_label || yAxisField;
            
            return `${datasetLabel}: ${xFieldLabel}=${point.x.toLocaleString()}, ${yFieldLabel}=${point.y.toLocaleString()}`;
          }
        }
      },
      datalabels: {
        display: chartConfig.showLabels || false,
        color: chartConfig.labelColor || '#000000',
        font: {
          size: 12,
          weight: 'bold'
        },
        formatter: function(value, context) {
          // Show the group field value as label
          const label = value?.label || '';
          console.log('Datalabels formatter called:', { value, context, label, showLabels: chartConfig.showLabels });
          return label;
        },
        anchor: 'end',
        align: 'top',
        offset: 6
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: chartConfig.scatterXAxisLabel || summarizedData?._metadata?.summarized_fields?.[xAxisField]?.field_label || xAxisField
        },
        beginAtZero: true
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: chartConfig.scatterYAxisLabel || summarizedData?._metadata?.summarized_fields?.[yAxisField]?.field_label || yAxisField
        },
        beginAtZero: true
      }
    },
    elements: {
      point: {
        radius: 6,
        hoverRadius: 8,
        borderWidth: 2
      }
    }
  };
  
  console.log('Scatter chart data being rendered:', scatterChartData);
  console.log('Scatter chart options:', scatterOptions);
  
  return (
    <div className="chart-container">
      <div className="chart-title">{chartConfig.title || chartTitle}</div>
      <div className="chart-render">
        <Scatter data={scatterChartData} options={scatterOptions} />
      </div>
    </div>
  );
};

export default ScatterChart;
